import Vue from 'vue'
import VueRouter from 'vue-router'
import Home from '../views/Home.vue'
import store from '../store'
import { READ_ONLY_LANDING } from '../featureFlags'
import { trackPageView } from '@/services/analytics.js';

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue')
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/Register.vue')
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('../views/ForgotPassword.vue')
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue')
  },
  {
    path: '/complete-profile',
    name: 'CompleteProfile',
    component: () => import('../views/CompleteProfile.vue'),
    meta: { requiresAuth: true, allowIncompleteProfile: true }
  },
  {
    path: '/vehicles',
    name: 'VehicleList',
    component: () => import('../views/vehicle/VehicleList.vue'),
  },
  {
    path: '/vehicles/:id',
    name: 'VehicleDetail',
    component: () => import('../views/vehicle/VehicleDetail.vue'),
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/user/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    redirect: '/dashboard'
  },
  {
    path: '/my-vehicles',
    name: 'MyVehicles',
    component: () => import('../views/vehicle/MyVehicles.vue'),
    meta: { requiresAuth: true, requiresSeller: true }
  },
  {
    path: '/add-vehicle',
    name: 'AddVehicle',
    component: () => import('../views/vehicle/AddVehicle.vue'),
    meta: { requiresAuth: true, requiresSeller: true }
  },
  {
    path: '/edit-vehicle/:id',
    name: 'EditVehicle',
    component: () => import('../views/vehicle/AddVehicle.vue'),
    props: true,
    meta: { requiresAuth: true, requiresSeller: true }
  },
  {
    path: '/my-offers',
    name: 'MyOffers',
    component: () => import('../views/offer/MyOffers.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/my-deposits',
    name: 'MyDeposits',
    component: () => import('../views/deposit/MyDeposits.vue'),
    meta: { requiresAuth: true, requiresBuyer: true }
  },
  {
    path: '/staff',
    name: 'AdminDashboard',
    component: () => import('../views/admin/Dashboard.vue'),
    meta: { 
      requiresAuth: true,
      requiresStaff: true 
    }
  },
  {
    path: '/staff/marketing',
    name: 'MarketingHub',
    component: () => import('../views/admin/marketing/MarketingHub.vue'),
    meta: {
      requiresAuth: true,
      requiresStaff: true
    }
  },
  {
    path: '/staff/marketing/audiences/new',
    name: 'AudienceCreate',
    component: () => import('../views/admin/marketing/AudienceFlow.vue'),
    meta: {
      requiresAuth: true,
      requiresStaff: true
    }
  },
  {
    path: '/staff/marketing/audiences/:id/edit',
    name: 'AudienceEdit',
    component: () => import('../views/admin/marketing/AudienceFlow.vue'),
    meta: {
      requiresAuth: true,
      requiresStaff: true
    }
  },
  {
    path: '/staff/marketing/campaigns/new',
    name: 'CampaignCreate',
    component: () => import('../views/admin/marketing/CampaignFlow.vue'),
    meta: {
      requiresAuth: true,
      requiresStaff: true
    }
  },
  {
    path: '/staff/marketing/campaigns/:id/edit',
    name: 'CampaignEdit',
    component: () => import('../views/admin/marketing/CampaignFlow.vue'),
    meta: {
      requiresAuth: true,
      requiresStaff: true
    }
  },
  {
    path: '/staff/marketing/campaigns/:id',
    name: 'CampaignDetail',
    component: () => import('../views/admin/marketing/CampaignDetailView.vue'),
    meta: {
      requiresAuth: true,
      requiresStaff: true
    }
  },
  {
    path: '/staff/whatsapp-crm',
    redirect: '/staff/marketing'
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue')
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('../views/Privacy.vue')
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('../views/Terms.vue')
  },
  {
    path: '/faqs',
    name: 'FAQ', 
    component: () => import('../views/FAQ.vue')
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue')
  },
  {
    path: '*',
    redirect: '/404'
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 }
  }
})

// Navigation Guard
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresStaff = to.matched.some(record => record.meta.requiresStaff)
  const requiresBuyer = to.matched.some(record => record.meta.requiresBuyer)
  const requiresSeller = to.matched.some(record => record.meta.requiresSeller)
  const isAuthenticated = store.getters.isAuthenticated

  // Modo landing: solo Home y About públicos para no autenticados
  if (READ_ONLY_LANDING && !isAuthenticated) {
    const allowedPublicNames = ['Home', 'About', 'Privacy', 'Terms', 'FAQ']
    if (!allowedPublicNames.includes(to.name)) {
      return next('/')
    }
  }
  
  // Si la ruta requiere login y no hay token, redirigir a login
  if (requiresAuth && !isAuthenticated) {
    return next('/login')
  }
  
  // Si la ruta requiere ser comprador
  if (requiresBuyer && isAuthenticated) {
    // Asegurarse de que el perfil esté cargado
    let currentUser = store.getters.currentUser
    if (!currentUser) {
      try {
        await store.dispatch('fetchUserProfile')
        currentUser = store.getters.currentUser
      } catch (error) {
        console.error('Error fetching user profile:', error)
        return next('/')
      }
    }
    
    // El staff puede acceder a cualquier ruta sin restricciones de tipo de usuario
    const isStaff = currentUser && (currentUser.is_staff || currentUser.is_superuser)
    if (isStaff) {
      // Staff tiene acceso completo, continuar
    } else {
      // Comprobar si el usuario es comprador
      const isBuyer = currentUser && currentUser.user_type === 'buyer'
      if (!isBuyer) {
        store.dispatch('setNotification', {
          type: 'error',
          message: 'Esta funcionalidad está disponible solo para compradores.'
        })
        return next('/dashboard')
      }
    }
  }
  
  // Si la ruta requiere ser vendedor
  if (requiresSeller && isAuthenticated) {
    // Asegurarse de que el perfil esté cargado
    let currentUser = store.getters.currentUser
    if (!currentUser) {
      try {
        await store.dispatch('fetchUserProfile')
        currentUser = store.getters.currentUser
      } catch (error) {
        console.error('Error fetching user profile:', error)
        return next('/')
      }
    }
    
    // El staff puede acceder a cualquier ruta sin restricciones de tipo de usuario
    const isStaff = currentUser && (currentUser.is_staff || currentUser.is_superuser)
    if (isStaff) {
      // Staff tiene acceso completo, continuar
    } else {
      // Comprobar si el usuario es vendedor
      const isSeller = currentUser && currentUser.user_type === 'seller'
      if (!isSeller) {
        store.dispatch('setNotification', {
          type: 'error',
          message: 'Esta funcionalidad está disponible solo para vendedores.'
        })
        return next('/dashboard')
      }
    }
  }
  
  // Si la ruta requiere permisos de staff
  if (requiresStaff) {
    // Asegurarse de que el perfil esté cargado
    let currentUser = store.getters.currentUser
    if (!currentUser && isAuthenticated) {
      try {
        await store.dispatch('fetchUserProfile')
        currentUser = store.getters.currentUser
      } catch (error) {
        console.error('Error fetching user profile:', error)
      }
    }
    
    // Comprobar si el usuario tiene permisos de staff
    const isStaff = currentUser && (currentUser.is_staff || currentUser.is_superuser)
    if (!isStaff) {
      store.dispatch('setNotification', {
        type: 'error',
        message: 'No tienes permisos para acceder a esta página.'
      })
      return next('/')
    }
  }
  
  // Verificar si el usuario autenticado tiene perfil completo
  if (isAuthenticated && to.name !== 'CompleteProfile' && to.name !== 'Login') {
    // Verificar si la ruta permite perfiles incompletos
    const allowIncompleteProfile = to.matched.some(record => record.meta.allowIncompleteProfile)
    
    if (!allowIncompleteProfile) {
      // Asegurarse de que el perfil esté cargado
      let currentUser = store.getters.currentUser
      if (!currentUser) {
        try {
          await store.dispatch('fetchUserProfile')
          currentUser = store.getters.currentUser
        } catch (error) {
          console.error('Error fetching user profile:', error)
          return next()
        }
      }
      
      // Verificar completitud del perfil
      if (currentUser) {
        // Si el usuario no tiene teléfono o no está en formato correcto, redirigir
        const hasValidPhone = currentUser.phone_number && currentUser.phone_number.startsWith('+549')
        
        if (!hasValidPhone) {
          return next('/complete-profile')
        }
      }
    }
  }
  
  // En cualquier otro caso, permitir acceso
  next()
})

router.afterEach((to) => {
  trackPageView(to.path, to.name);
});

export default router
